import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Button, Drawer, Row, Select, Space, Typography } from 'antd';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import isFunction from 'lodash/isFunction';
import map from 'lodash/map';
import find from 'lodash/find';
import sortBy from 'lodash/sortBy';
import uniq from 'lodash/uniq';
import { setShowAIConversation } from '@store/reducers/commonReducer';
import { useAIConversation } from '@hooks/useAIConversation';
import { useCommon } from '@hooks/useCommon';
import { useAuth } from '@hooks/useAuth';
import { CourseData } from '@store/reducers/aiConversationReducer';

const HANDSHAKE_INTERVAL = 200;
const HANDSHAKE_TIMEOUT = 15000;
// Types the embedded app may send back; any of them proves its listener is up.
const IFRAME_READY_TYPES = ['set-action-ack', 'ready', 'iframe-ready', 'app-ready'];

export interface AIConversationRef {
  sendMessage: (message: string) => void;
  open: () => void;
  close: () => void;
}

interface AIConversationProps {
  onSendMessage?: (message: string) => void;
  question_id?: string;
  question_type?: string;
  mode?: 'vteacher' | 'vmentor';
  layout?: 'drawer' | 'container';
  /** Raise drawer above fullscreen shells (e.g. offline exercise z-[1100]) */
  zIndex?: number;
}

const AIConversation = React.forwardRef<AIConversationRef, AIConversationProps>(
  (
    {
      onSendMessage,
      question_id,
      question_type,
      mode = 'vmentor',
      layout = 'drawer',
      zIndex,
    },
    ref,
  ) => {
    const dispatch = useDispatch();
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const { showAIConversation } = useCommon();
    const [open, setOpen] = useState(false);
    const { auth } = useAuth();
    const { i18n } = useTranslation();
    const [collapsed, setCollapsed] = useState(false);
    const [selectedSubject, setSelectedSubject] = useState<string | null>(null);
    const {
      courseList,
      currentCourse,
      vteacherUrl,
      getVTeacherUrl,
      getCourseList,
      setCurrentCourse,
    } = useAIConversation();
    const intervalSendDataToIframe = useRef<NodeJS.Timeout | null>(null);
    // The iframe app registers its `message` listener after its own bundle boots,
    // so the very first postMessage can land before anyone is listening.
    // We retry until the iframe acks (or announces itself ready), then stop.
    const iframeLoadedRef = useRef(false);
    const ackedRef = useRef(false);

    React.useImperativeHandle(ref, () => ({
      sendMessage: (message: string) => {
        isFunction(onSendMessage) && onSendMessage(message);
      },
      open: () => {
        setOpen(true);
      },
      close: () => {
        setOpen(false);
      },
    }));

    const onCloseH5PAi = () => {
      dispatch(setShowAIConversation(false));
      setOpen(false)
    };

    useEffect(() => {
      getCourseList();
    }, []);

    const hasSubject = useMemo(() => find(courseList, 'subject'), [courseList])

    // Get unique subjects from courseList
    const availableSubjects = useMemo((): string[] => {
      if (!courseList || courseList.length === 0) return [];

      const subjects = courseList
        .map((course: CourseData) => course.subject || 'Other')
        .filter(Boolean) as string[];

      const uniqueSubjects = uniq(subjects) as string[];

      // Sort so "Other" appears at the end
      return uniqueSubjects.sort((a, b) => {
        if (a === 'Other') return 1;
        if (b === 'Other') return -1;
        return 0;
      });
    }, [courseList]);

    // Filter courses based on selected subject
    const filteredCourseList = useMemo(() => {
      if (!selectedSubject) return courseList;

      return courseList.filter((course: CourseData) => {
        const courseSubject = course.subject || 'Other';
        return courseSubject === selectedSubject;
      });
    }, [courseList, selectedSubject]);

    // Set default subject to first available subject
    useEffect(() => {
      // console.log('hasSubject', hasSubject);
      // console.log('availableSubjects', availableSubjects);
      // console.log('courseList', courseList);
      if (hasSubject && availableSubjects.length > 0) {
        const firstSubject = availableSubjects[0] as string;
        setSelectedSubject(firstSubject);
        setCurrentCourse(find(courseList, { subject: firstSubject }));
      }
    }, [hasSubject, availableSubjects, courseList]);

    const sendDataToIframe = (currentCourse: CourseData) => {
      let level = 'tieu_hoc';
      if (currentCourse.grade_value >= 1 && currentCourse.grade_value <= 5) {
        level = 'tieu_hoc';
      } else if (
        currentCourse.grade_value >= 6 &&
        currentCourse.grade_value <= 9
      ) {
        level = 'trung_hoc_co_so';
      } else if (
        currentCourse.grade_value >= 10 &&
        currentCourse.grade_value <= 12
      ) {
        level = 'trung_hoc_pho_thong';
      }

      if (iframeRef.current && currentCourse) {
        iframeRef.current.contentWindow?.postMessage(
          {
            type: 'set-action',
            data: {
              level,
              grade: `${currentCourse.grade_value}`,
              mode,
            },
          },
          '*',
        );
      }
    };

    const stopHandshake = () => {
      if (intervalSendDataToIframe.current) {
        clearInterval(intervalSendDataToIframe.current);
        intervalSendDataToIframe.current = null;
      }
    };

    // Keep the latest course in a ref so the retry timer and the message
    // listener never send a stale payload.
    const currentCourseRef = useRef<CourseData | undefined>(currentCourse);
    currentCourseRef.current = currentCourse;

    const startHandshake = () => {
      stopHandshake();

      const course = currentCourseRef.current;
      if (!iframeLoadedRef.current || !iframeRef.current || !course) return;

      sendDataToIframe(course);

      // Already acked once: the iframe is listening, a single send is enough.
      if (ackedRef.current) return;

      let elapsed = 0;
      intervalSendDataToIframe.current = setInterval(() => {
        elapsed += HANDSHAKE_INTERVAL;
        const nextCourse = currentCourseRef.current;
        if (ackedRef.current || elapsed >= HANDSHAKE_TIMEOUT || !nextCourse) {
          stopHandshake();
          return;
        }
        sendDataToIframe(nextCourse);
      }, HANDSHAKE_INTERVAL);
    };

    const onIframeLoad = () => {
      iframeLoadedRef.current = true;
      startHandshake();
    };

    // A remounted iframe (vteacher rebuilds its src) is a brand new document
    // with no listener yet, so the handshake has to start over.
    const attachIframe = (node: HTMLIFrameElement | null) => {
      if (node !== iframeRef.current) {
        stopHandshake();
        iframeLoadedRef.current = false;
        ackedRef.current = false;
      }
      iframeRef.current = node;
    };

    // Stop retrying as soon as the iframe answers; send straight away if it
    // announces it is ready before we managed to reach it.
    useEffect(() => {
      const onMessage = (event: MessageEvent) => {
        if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) {
          return;
        }

        const type = event?.data?.type;
        if (!IFRAME_READY_TYPES.includes(type)) return;

        ackedRef.current = true;
        stopHandshake();

        if (type !== 'set-action-ack' && currentCourseRef.current) {
          sendDataToIframe(currentCourseRef.current);
        }
      };

      window.addEventListener('message', onMessage);
      return () => window.removeEventListener('message', onMessage);
    }, [mode]);

    // Course arriving (or changing) after the iframe has loaded must be pushed too.
    useEffect(() => {
      startHandshake();
    }, [currentCourse, mode]);

    useEffect(() => {
      return () => {
        stopHandshake();
      };
    }, []);

    // The launch URL is per-course (different courses can use different
    // VTeacher deployments), so re-fetch it from the backend whenever the
    // selected course changes.
    useEffect(() => {
      if (currentCourse?.id) {
        getVTeacherUrl(currentCourse.id);
      }
    }, [currentCourse?.id]);

    const iframeVTeacher = useMemo(
      () => {
        if (!vteacherUrl) return undefined;

        return <iframe
          ref={attachIframe}
          id='ai-tutor-iframe'
          onLoad={onIframeLoad}
          src={vteacherUrl}
          allow='microphone; camera; autoplay; clipboard-read; clipboard-write'
          allowFullScreen
          width='100%'
          height='100%'
          style={{
            overflow: 'scroll',
          }}
        />
      },
      [vteacherUrl],
    );

    const renderSelect = () => (
      <Space className='mb-4'>
        {hasSubject && (
          <Select
            className='subject-select'
            style={{
              width: '150px !important',
              minWidth: '150px !important',
            }}
            value={selectedSubject}
            options={map(availableSubjects, (subject) => ({
              value: subject,
              label: subject,
            }))}
            onChange={(value) => {
              setSelectedSubject(value);
              // Reset current course when subject changes
              const coursesForSubject = courseList.filter(
                (course: CourseData) => (course.subject || 'Other') === value
              );
              if (coursesForSubject.length > 0) {
                setCurrentCourse(coursesForSubject[0]);
              }
            }}
            placeholder='Chưa chọn môn'
          />
        )
        }
        <Select
          className='course-select'
          style={{
            width: '150px !important',
            minWidth: '150px !important',
          }}
          value={currentCourse?.course_id}
          options={map(sortBy(filteredCourseList, ['id']), (course) => ({
            value: course.course_id,
            label: course.course_name,
          }))}
          onChange={(value) => {
            const selectedCourse = find(courseList, {
              course_id: value,
            }) || {
              ...courseList[0],
              course_id: undefined,
            };

            setCurrentCourse(selectedCourse);
            // Update subject selector to match the selected course's subject
            const courseSubject = selectedCourse.subject || 'Other';
            setSelectedSubject(courseSubject);
          }}
          allowClear
          placeholder='Chưa chọn chương'
        />
      </Space>

    )

    const renderLayout = (children: React.ReactNode) => {
      if (layout === 'drawer') {
        return <Drawer
          title='Trợ lý ảo học tập'
          placement='right'
          closable={false}
          onClose={onCloseH5PAi}
          open={open || showAIConversation}
          width={'66vw'}
          zIndex={zIndex}
          extra={
            <Space>
              {currentCourse && (
                <Typography>Lớp: {currentCourse.grade_value}</Typography>
              )}
              {mode === 'vteacher' && courseList && courseList.length > 0 && (
                renderSelect()
              )}
              <Button
                type='primary'
                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setCollapsed((prev) => !prev)}
              />
            </Space>
          }
        >
          {children}
        </Drawer>
      }

      if (layout === 'container') {
        return (
          <div
            style={{
              height: '100%',
              width: '100%',
              marginTop: '-22px',
            }}
          >
            <Row>
              {
                renderSelect()
              }
            </Row>
            <Row className='h-full'>{children}</Row>
          </div>
        )
      }
    }

    return (
      renderLayout(<div
        style={{
          height: '100%',
          width: '100%',
        }}
      >
        {mode === 'vmentor' && (
          <iframe
            ref={attachIframe}
            id='ai-tutor-iframe'
            onLoad={onIframeLoad}
            src={`${process.env.REACT_AI_TUTOR_URL}?${new URLSearchParams({
              userId: `${auth?.user_id}`,
              showChat: 'true',
              appId: `${process.env.REACT_APP_ID}`,
              language: `${i18n.language.split('-')[0]}`,
            }).toString()}`}
            width='100%'
            height='100%'
          />
        )}
        {mode === 'vteacher' && iframeVTeacher}
      </div>)
    );
  },
);

AIConversation.displayName = 'AIConversation';

export default AIConversation;
